/**
 * The flight scene's half of AD-015's kill camera: the boom that follows a
 * falling wreck, and the second scissored pass that draws the corner window.
 *
 * Split out of `flight-scene.ts` for the reason `scene-propellers.ts` and
 * `scene-flyby-zoom.ts` were — the host sits close to aislop's size ceiling and
 * a cohesive addition belongs in a sibling. `flight-scene.ts` keeps one field
 * and one constructor call; `scene-draw.ts` makes the two frame-loop calls, in
 * the order the pass requires. The *rules* — when the window opens and shuts,
 * and what rectangle it occupies — are in `killcam.ts`, which imports neither
 * `three` nor the DOM.
 *
 * ## Why the pass is shaped the way it is
 *
 * `setScissorTest` restricts *draws*, not *clears*. WebGPU clears through a
 * render pass's `loadOp`, which applies to the whole attachment however small
 * the scissor rectangle is — so the obvious "set a scissor and render again"
 * wipes the main picture and leaves only the inset. The fix is one flag:
 * `autoClearColor = false` makes the second pass load the colour attachment
 * instead of clearing it (three's `common/Background.js` copies `autoClearColor`
 * onto the render context, which the WebGPU backend turns into
 * `GPULoadOp.Load`), while `autoClearDepth` stays on so the inset gets a clean
 * depth buffer to draw a completely different view into. Wiping the whole depth
 * buffer costs nothing: the main view's colour has already been resolved into
 * the attachment, and nothing draws after this.
 *
 * The rectangle is in CSS pixels from the **top-left**, which is what
 * `Renderer.setViewport` documents and what the WebGPU backend passes straight
 * to `setScissorRect`; the WebGL fallback backend flips it itself. That is also
 * why `.killcam-frame`'s CSS can be plain `top`/`right` percentages.
 */

import * as THREE from "three/webgpu";
import { isOver, type Vec3 } from "@red-opus/engine";
import { groundHeadingOf, KILL_CAM_CAMERA, killCamGoal, smoothFollow } from "./camera-follow.ts";
import type { CombatVisuals } from "./combat-visuals.ts";
import type { EffectsSettings } from "./effects-settings.ts";
import type { AircraftView } from "./encounter-views.ts";
import { buildCamera } from "./flight-view.ts";
import { resolveKillCamRect, type KillCamPlacement } from "./killcam-layout.ts";
import { CLOSED_KILL_CAM, KILL_CAM_LAYOUT, nextKillCamState, type KillCamRect, type KillCamState } from "./killcam.ts";
import type { ParticleEffects } from "./scene-particles.ts";
import type { EncounterRunner } from "./sim-runner.ts";
import type { SpriteEffects, SpriteFrame } from "./sprite-effects.ts";

/**
 * The window's own camera and the smoothing state behind it. Separate from
 * `CameraRig` on purpose: the main camera keeps flying wherever the player put
 * it while this one goes to watch somebody die, and sharing one smoothed
 * position would drag the main view along with it.
 */
export interface KillCamRig {
  readonly camera: THREE.PerspectiveCamera;
  /** Where the boom has smoothed to, in world space. */
  readonly position: THREE.Vector3;
  /** The victim's ground heading, frozen when the window opened — see `camera-follow.ts`'s `KillCamSpec`. */
  bearing: Vec3;
  /** Whom `position` and `bearing` were seeded for. A change re-seeds both, so a re-target cuts rather than smears. */
  victimId: string | null;
}

/** Reused by `renderKillCam`, which runs every frame the window is open — `getSize` fills whatever it is handed. */
const SCRATCH_SIZE = new THREE.Vector2();

function createKillCamRig(): KillCamRig {
  const camera = buildCamera();
  camera.aspect = KILL_CAM_LAYOUT.aspect;
  camera.updateProjectionMatrix();
  return { camera, position: new THREE.Vector3(), bearing: { x: 0, y: 0, z: -1 }, victimId: null };
}

/** Everything the corner window owns, in one field of `flight-scene.ts`'s state rather than three. */
export interface KillCamWindow {
  state: KillCamState;
  readonly rig: KillCamRig;
  /** Last openness reported through `onKillCam`, so the route hears edges only. */
  shown: boolean;
  /**
   * Where the pilot has put the window, or `null` for `killcam.ts`'s default
   * corner. Replaced (never mutated) by `setKillCamPlacement`, which is what
   * lets `rectFor` below compare it by identity.
   */
  placement: KillCamPlacement | null;
  /**
   * Shut by its own × for the rest of this sortie. Deliberately **not** the
   * KILL CAMERA setting and deliberately not persisted — see
   * `docs/engineering/killcam-design.md` §"Closing versus switching off".
   */
  dismissed: boolean;
  /**
   * `resolveKillCamRect`'s last answer and the three inputs it was computed
   * from. The pass runs every frame the window is open and the answer changes
   * only when the pilot drags something or the view is resized, so this is one
   * object allocated per change rather than one per frame.
   */
  rect: KillCamRect | null;
  rectFor: { width: number; height: number; placement: KillCamPlacement | null };
}

export function createKillCamWindow(placement: KillCamPlacement | null = null): KillCamWindow {
  return {
    state: CLOSED_KILL_CAM,
    rig: createKillCamRig(),
    shown: false,
    placement,
    dismissed: false,
    rect: null,
    // A width of 0 can never be a real view, so the first call always computes.
    rectFor: { width: 0, height: 0, placement },
  };
}

/** Moves or resizes the window live, from a drag in `KillCamFrame.tsx`. */
export function setKillCamPlacement(window: KillCamWindow, placement: KillCamPlacement | null): void {
  window.placement = placement;
}

/** The × on the title bar: no more windows until the next sortie, or until KILL CAMERA is switched back on. */
export function dismissKillCam(window: KillCamWindow): void {
  window.dismissed = true;
}

/** Turning KILL CAMERA on in PREFERENCES clears a dismissal — the toggle is the authority, and this is the way back. */
export function restoreKillCam(window: KillCamWindow): void {
  window.dismissed = false;
}

/**
 * The window's rectangle in a view of this size, memoised. Exported so the "one
 * object per change, not one per frame" claim above is an assertion rather than
 * a comment (`killcam-window.test.ts`).
 */
export function killCamRectFor(corner: KillCamWindow, width: number, height: number): KillCamRect | null {
  const cached = corner.rectFor;
  if (cached.width === width && cached.height === height && cached.placement === corner.placement) return corner.rect;
  cached.width = width;
  cached.height = height;
  cached.placement = corner.placement;
  corner.rect = resolveKillCamRect(corner.placement, width, height);
  return corner.rect;
}

/** What `flight-scene.ts`'s own state has to offer for the window to work. Structural, like `PropellerHost`. */
export interface KillCamHost {
  readonly scene: THREE.Scene;
  readonly renderer: THREE.WebGPURenderer;
  readonly runner: EncounterRunner;
  readonly sprites: SpriteEffects;
  /** ENHANCED EFFECTS' two clouds — re-emitted for this lens, like the sprites (`renderKillCam`). */
  readonly particles: ParticleEffects;
  readonly combat: CombatVisuals;
  /** Read live, so the KILL CAMERA toggle takes effect on the next frame like PROPELLER EFFECT does. */
  readonly effects: EffectsSettings;
  /** Fixed at scene creation — `/duel` turns the feature off entirely (`FlightSceneOptions.killCamAvailable`). */
  readonly killCamAvailable: boolean;
  readonly killCam: KillCamWindow;
  readonly missionEnded: boolean;
  readonly dogfightEnded: boolean;
}

/**
 * True once nothing more will happen in the sky. Three sources because three
 * things freeze the runner: a decided encounter, a decided mission, and the
 * instant dogfight's own verdict — and a window left open over a frozen
 * aeroplane would hang there for as long as the player left the debrief up.
 */
function flightEnded(host: KillCamHost): boolean {
  return host.missionEnded || host.dogfightEnded || isOver(host.runner.encounter().status);
}

function placeKillCam(rig: KillCamRig, victim: AircraftView, dt: number): void {
  if (rig.victimId !== victim.id) {
    rig.victimId = victim.id;
    rig.bearing = groundHeadingOf(victim.pose.orientation);
    const seed = killCamGoal(victim.pose, rig.bearing);
    rig.position.set(seed.x, seed.y, seed.z);
  }
  const goal = killCamGoal(victim.pose, rig.bearing);
  const smoothed = smoothFollow(rig.position, goal, KILL_CAM_CAMERA.followRate, dt);
  rig.position.set(smoothed.x, smoothed.y, smoothed.z);
  rig.camera.position.copy(rig.position);
  // World up, never the wreck's own: a tumbling airframe would otherwise roll
  // the horizon out of the window, which is the same call the two external
  // views already make (`flight-view.ts`'s `placeCamera`).
  rig.camera.up.set(0, 1, 0);
  rig.camera.lookAt(victim.pose.position.x, victim.pose.position.y, victim.pose.position.z);
}

/**
 * Advances the window's state machine over this frame's roster and, when it is
 * open, places its camera. Returns the victim being watched, or `null` — which
 * is the whole of what the frame loop has to branch on, and what keeps the
 * second pass costing exactly nothing while the window is shut.
 */
export function updateKillCam(host: KillCamHost, dt: number): AircraftView | null {
  const others = host.runner.others();
  const corner = host.killCam;
  corner.state = nextKillCamState(corner.state, {
    enabled: host.killCamAvailable && host.effects.killCamera && !corner.dismissed,
    aircraft: others,
    dt,
    ended: flightEnded(host),
  });
  const watched = corner.state.victimId;
  if (watched === null) {
    corner.rig.victimId = null;
    return null;
  }
  const victim = others.find((entry) => entry.id === watched);
  if (!victim) return null;
  placeKillCam(corner.rig, victim, dt);
  return victim;
}

/**
 * Draws the corner window over the finished main view and tells the route
 * whether it is up — the frame loop's whole second half of this feature, in one
 * call. `victim` is what `updateKillCam` returned: `null` means the window is
 * shut, and then this costs one comparison and nothing else.
 *
 * The route is told on edges only. It draws the window's border, and stands the
 * controls hint (and, in the cockpit, the painted panel) out of the corner.
 */
export function presentKillCam(
  host: KillCamHost,
  victim: AircraftView | null,
  fovYRadians: number,
  frame: SpriteFrame,
  report: ((open: boolean) => void) | undefined,
): void {
  if (victim) renderKillCam(host, fovYRadians, frame);
  if ((victim !== null) === host.killCam.shown) return;
  host.killCam.shown = victim !== null;
  report?.(host.killCam.shown);
}

/**
 * The pass itself.
 *
 * The sprites, the particles and the bullet quads are re-emitted for this lens
 * first. All three are CPU-billboarded against one camera per frame
 * (`sprite-effects.ts`, `three/particle-cloud.ts`, `combat-visuals.ts`), so
 * drawing them from a second camera without rebuilding them shows every
 * explosion, flame and smoke puff edge-on — and the crash the window exists to
 * show is made of exactly those. Nothing is *advanced* by the rebuild: the
 * animation has already moved once this frame in the main pass, and this is the
 * same instant seen from somewhere else. The sprites say that with `dt = 0`;
 * the particles have a `redraw` that says it in the name.
 */
function renderKillCam(host: KillCamHost, fovYRadians: number, frame: SpriteFrame): void {
  const size = host.renderer.getSize(SCRATCH_SIZE);
  const rect = killCamRectFor(host.killCam, size.x, size.y);
  if (!rect) return;
  const { camera } = host.killCam.rig;
  camera.aspect = rect.width / rect.height;
  camera.updateProjectionMatrix();
  host.sprites.update(0, camera, fovYRadians, frame);
  host.particles.redraw(camera);
  host.combat.update(host.runner.rounds(), camera);

  const { renderer } = host;
  renderer.autoClearColor = false;
  renderer.setScissorTest(true);
  renderer.setScissor(rect.x, rect.y, rect.width, rect.height);
  renderer.setViewport(rect.x, rect.y, rect.width, rect.height);
  renderer.render(host.scene, camera);
  renderer.setViewport(0, 0, size.x, size.y);
  renderer.setScissorTest(false);
  renderer.autoClearColor = true;
}
